/**
 * useSemanticSearch
 *
 * Debounces the query string (300 ms) and calls the semantic search API via
 * TanStack Query.  Returns null results when the query is empty so callers can
 * fall through to their normal view-filtered task list.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Task } from '../types';
import { searchTasks } from '../api/search';

export interface SemanticSearchState {
  results: Task[] | null;
  mode: 'semantic' | 'keyword' | null;
  isLoading: boolean;
  isError: boolean;
}

export function useSemanticSearch(query: string): SemanticSearchState {
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(id);
  }, [query]);

  const enabled = debouncedQuery.length >= 2;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['semantic-search', debouncedQuery],
    queryFn: () => searchTasks(debouncedQuery),
    enabled,
    staleTime: 30_000,
  });

  if (!enabled) {
    return { results: null, mode: null, isLoading: false, isError: false };
  }

  return {
    results: data?.tasks ?? null,
    mode: data?.isSemanticMode ? ('semantic' as const) : ('keyword' as const),
    isLoading,
    isError,
  };
}
